import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth, API } from '@/App';
import axios from 'axios';
import { Ear, ArrowLeft, Package } from '@phosphor-icons/react';

const CATEGORY_LABELS = {
  embout_silicone: 'Embouts Silicone',
  embout_resine: 'Embouts Résine 3D',
  obturateur: 'Obturateurs',
  option: 'Options',
  option_obturateur: 'Options Obturateur',
  option_embout: 'Options Embout',
  montage: 'Montages',
};

export default function Catalog() {
  const { user, refreshUser } = useAuth();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const canViewPrices = user?.can_view_prices === true;

  useEffect(() => {
    refreshUser();
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API}/pricing`);
      setProducts(response.data.products || []);
    } catch {
      try {
        const fallback = await axios.get(`${API}/products`);
        setProducts(fallback.data || []);
      } catch { /* silent */ }
    } finally {
      setLoading(false);
    }
  };

  const grouped = products.reduce((acc, p) => {
    const cat = p.category || 'autre';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(p);
    return acc;
  }, {});

  const categoryOrder = ['embout_silicone', 'embout_resine', 'obturateur', 'option_obturateur', 'option_embout', 'option', 'montage'];
  const sortedCategories = Object.keys(grouped).sort((a, b) => {
    const ia = categoryOrder.indexOf(a);
    const ib = categoryOrder.indexOf(b);
    return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib);
  });

  return (
    <div className="min-h-screen bg-zinc-100" data-testid="catalog-page">
      <header className="bg-white border-b border-zinc-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Ear size={28} weight="duotone" className="text-zinc-900" />
            <span className="font-heading font-bold text-lg tracking-tight">HAMAC SOLUTIONS</span>
          </div>
          <Link to="/client">
            <button className="flex items-center gap-2 text-zinc-600 hover:text-zinc-900 text-sm" data-testid="catalog-back-btn">
              <ArrowLeft size={18} />
              Retour
            </button>
          </Link>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        <div className="mb-6">
          <h1 className="font-heading text-2xl font-bold tracking-tight mb-1">Catalogue Produits</h1>
          <p className="text-zinc-600 text-sm">Découvrez notre gamme d'embouts auditifs sur mesure</p>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="spinner mx-auto mb-4" />
            <p className="text-zinc-500">Chargement...</p>
          </div>
        ) : products.length === 0 ? (
          <div className="bg-white border border-zinc-200 p-12 text-center">
            <Package size={48} className="mx-auto mb-4 text-zinc-300" weight="duotone" />
            <p className="text-zinc-500">Catalogue en cours de mise à jour</p>
          </div>
        ) : (
          <div className="space-y-8">
            {sortedCategories.map(cat => (
              <div key={cat} data-testid={`category-${cat}`}>
                <h2 className="font-heading font-semibold text-base text-zinc-800 mb-3 uppercase tracking-wider border-b border-zinc-200 pb-2">
                  {CATEGORY_LABELS[cat] || cat}
                </h2>
                <div className="grid gap-3">
                  {grouped[cat].map(product => {
                    const hasDiscount = canViewPrices && product.discount_pct > 0;
                    return (
                      <div
                        key={product.id}
                        className="bg-white border border-zinc-200 p-4 sm:p-5 flex items-center justify-between hover:border-zinc-300 transition-colors"
                        data-testid={`product-${product.id}`}
                      >
                        <div className="flex-1 min-w-0">
                          <h3 className="font-semibold text-sm text-zinc-900">{product.name}</h3>
                          {product.description && (
                            <p className="text-xs text-zinc-500 mt-0.5 truncate">{product.description}</p>
                          )}
                        </div>
                        {canViewPrices && (
                          <div className="flex items-center gap-2 sm:gap-3 ml-3 sm:ml-4 flex-shrink-0" data-testid={`price-${product.id}`}>
                            {hasDiscount ? (
                              <>
                                <span className="text-xs text-zinc-400 line-through" data-testid={`price-original-${product.id}`}>
                                  {product.base_price?.toFixed(2)} €
                                </span>
                                <span className="font-mono font-bold text-base text-zinc-900" data-testid={`price-final-${product.id}`}>
                                  {product.discounted_price?.toFixed(2)} €
                                </span>
                                <span className="text-[10px] bg-emerald-50 text-emerald-700 px-1.5 py-0.5 rounded-sm font-medium" data-testid={`discount-badge-${product.id}`}>
                                  -{product.discount_pct}%
                                </span>
                              </>
                            ) : (
                              <span className="font-mono font-bold text-base text-zinc-900" data-testid={`price-standard-${product.id}`}>
                                {(product.base_price || 0).toFixed(2)} €
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}

        {!canViewPrices && !loading && products.length > 0 && (
          <div className="mt-6 bg-zinc-50 border border-zinc-200 p-4 rounded-sm text-center" data-testid="no-prices-notice">
            <p className="text-sm text-zinc-500">
              Les prix ne sont pas affichés. Contactez votre administrateur pour activer l'affichage des tarifs.
            </p>
          </div>
        )}
      </main>
    </div>
  );
}
